import React, { useMemo } from 'react';
import { Modal } from 'antd';
import Subscription from 'components/Subscription';
import KlairtySubscription from 'components/KlaritySubscription';
import ManageSubscription from 'components/ManageSubscription';
import SubscriptionSuccess from 'components/SubscriptionSuccess';
import commonS from 'styles/common.module.less';
import {
    closeChannelSubscriptionModal,
    closeKlaritySubscriptionModal,
    closeManageSubscriptionModal,
    closeSubscriptionModal,
    closeSubscriptionSuccessModal,
    closeSubscriptionTalkModal,
    openUniprofileAgreementSignModal,
    closeBlackFriday2024EnhancementModal,
    closeBlackFriday2024JoinCampaignModal,
    closeTermsNConditionsModal,
    closeCCCTermsNConditionsModal,
    closeCampaignModal,
    closeCampaignTermsNConditionsModal,
    openKiwiPlanAdvertisingModal,
    closeTextVisitUnSubmittedDrawer,
    closeTextVisitSubmittedDrawer,
    closeVideoVisitSubmittedDrawer,
    closeVideoVisitUnSubmittedDrawer,
    closeNonAppointmentSubmittedDrawer,
} from 'utils/globalLayerControl';
import GlobalLayerStore from 'store/GlobalLayer';
import { isKlarityHost, isOpPortal, isLocalhost } from 'utils/common';
import SubscriptionTalk from 'components/SubscriptionTalk';
import ChannelSubscription from 'components/ChannelSubscription';
import { IS_BACK_FROM_STRIPE } from 'constants/common';
import KlarityPlanModal from 'components/KlarityPlanModal';
import SubscriptionStore from 'store/Subscription';
import s from './s.module.less';
import { setShowKiwiPlanAdvertise, getShowKiwiPlanAdvertise } from 'utils/sessionStorage';
import { setShowKiwiPlanAdvertiseTimes, getShowKiwiPlanAdvertiseTimes } from 'utils/localstore';
import { isFreeUserByInfo, shouldShowKlarityPlanUI } from 'utils/provider';
import EnhancementModal from './CampainModals/blackfriday2024/EnhancementModal';
import JoinCampaignModal from './CampainModals/blackfriday2024/JoinCampaignModal';
import LeavCampaignModal from './CampainModals/blackfriday2024/LeavCampaignModal';
import TermsNConditions from 'components/TermsNConditions';
import CCCTermsNConditions from 'components/TermsNConditions/CCC';
import CampaignTermsNConditions from 'components/TermsNConditions/template';
import JoinCampaignModalTemplate from './CampainModals/CampaignModalTemplate/JoinCampaignModalTemplate';
import LeavCampaignModalTemplate from './CampainModals/CampaignModalTemplate/LeavCampaignModalTemplate';
import { PATH } from 'constants/path';
import PPAChannelSubscription from 'components/PPAChannelSubscription';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import usePPAChanneld from 'hooks/usePPAChanneld';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { Helmet } from 'react-helmet';
import HomeStore from 'store/Home';
import VideoVisitSubmittedDrawer from 'components/VideoVisitSubmittedDrawer';
import VideoVisitUnSubmittedDrawer from 'components/VideoVisitUnSubmittedDrawer';
import NonAppointmentSubmittedVisitNoteDrawer from 'components/NonAppointmentSubmittedVisitNoteDrawer';

interface GlobalLayerProps {
    isShowKlarityUi: boolean;
}

const GlobalLayer = ({ isShowKlarityUi }: GlobalLayerProps) => {
    const [getGlobalLayer, setGlobalLayer] = GlobalLayerStore.useStore();
    const [getSubscription] = SubscriptionStore.useStore();
    const showKiwiPlanAdvertisingTimeIntervalFlag = getGlobalLayer('showKiwiPlanAdvertisingTimeIntervalFlag');
    const currentPlan = getSubscription('currentPlan');
    const showKiwiAdvertiseIntervalRef = React.useRef<number>();
    const [params] = useSearchParams();
    const channelId = params.getAll('channelId')[0];
    const [ppaId] = usePPAChanneld();

    const isOpValue = React.useMemo(() => isOpPortal(), []);
    const [data, loadingHome] = useProviderHomeInfo(isOpValue);
    const [getHomeStore] = HomeStore.useStore();
    const homeStoreData = getHomeStore('data');

    // Use HomeStore data as fallback if hook data is undefined
    const actualData = data || homeStoreData;
    const isKlarity = !!actualData?.klarityUser;
    const isShowSwitchAccount = useMemo(
        () => (loadingHome ? false : actualData?.providerTypes?.length === 2),
        [actualData, loadingHome],
    );
    const navigate = useNavigate();
    const isPPAChannel = React.useMemo(() => ppaId && +channelId === +ppaId, [channelId, ppaId]);

    const host = window.location.hostname;
    const path = window.location.pathname;

    React.useEffect(() => {
        if (!navigate || isLocalhost() || isOpPortal()) {
            return;
        }
        if (isKlarityHost()) {
            if (path === '/') {
                navigate(PATH.DASHBOARD, {
                    replace: true,
                });
            }
            if (path === `/${PATH.PROVIDER_LOGIN}`) {
                navigate(PATH.PPA_LOGIN, {
                    replace: true,
                });
            }
            if (path === `/${PATH.SIGNUP}` || path === `/${PATH.LOGIN}`) {
                navigate(PATH.PPA_LEAD_FORM, {
                    replace: true,
                });
            }
        } else {
            if (path === PATH.PPA_LOGIN) {
                navigate(`/${PATH.PROVIDER_LOGIN}`, {
                    replace: true,
                });
            }
            if (path === PATH.PPA_LEAD_FORM) {
                navigate(PATH.SIGNUP, {
                    replace: true,
                });
            }
        }
    }, [host, path]);

    React.useEffect(() => {
        if (currentPlan && isFreeUserByInfo(currentPlan)) {
            if (getShowKiwiPlanAdvertise() === 'idel') {
                setShowKiwiPlanAdvertise(true);
            }
            setGlobalLayer('showKiwiPlanAdvertisingTimeIntervalFlag', true);
        } else {
            //setShowKiwiPlanAdvertise(undefined);
            setGlobalLayer('showKiwiPlanAdvertisingTimeIntervalFlag', false);
        }
    }, [currentPlan]);

    // 根据isShowKlarityUi动态设置多个CSS变量
    React.useEffect(() => {
        const root = document.documentElement;

        // 定义需要动态切换的CSS变量
        const klarityTheme = {
            '--main-color': '#0E62F4',
            '--primary-color': '#0E62F4',
            '--primary-color-light': '#E2F1FF',
            '--main-blue': '#0E62F4',
            '--sider-bg': '#0E62F4',
            '--kiwi-primary-2': '#E0F2FE', // 浅蓝色背景
            '--kiwi-primary-6': '#0E62F4', // 主蓝色
            '--kiwi-primary-7': '#0E62F4', // 深蓝色
        };

        const defaultTheme = {
            '--main-color': '#00816B',
            '--primary-color': '#006957',
            '--primary-color-light': '#F0FCF6',
            '--main-blue': '#0E62F4',
            '--sider-bg': '#006957',
            '--kiwi-primary-2': '#DFEDE6', // 浅绿色背景
            '--kiwi-primary-6': '#006D51', // 主绿色
            '--kiwi-primary-7': '#005D4E', // 深绿色
        };

        // 根据isShowKlarityUi选择主题
        const theme = isShowKlarityUi ? klarityTheme : defaultTheme;

        // 批量设置CSS变量
        Object.entries(theme).forEach(([property, value]) => {
            root.style.setProperty(property, value);
        });

        if (isShowKlarityUi) {
            const src = '/favicon-klarity.ico';
            let link: any = document.querySelector("link[rel~='icon']");
            if (link) document.head.removeChild(link);
            link = document.createElement('link') as any;
            link.rel = 'icon';
            link.href = src;
            document.head.appendChild(link);
        }
    }, [isShowKlarityUi]);

    React.useEffect(() => {
        const callback = () => {
            if (getShowKiwiPlanAdvertise() !== true) {
                //cleared
                setGlobalLayer('showKiwiPlanAdvertisingTimeIntervalFlag', false);
                if (showKiwiAdvertiseIntervalRef.current) {
                    window.clearInterval(showKiwiAdvertiseIntervalRef.current);
                }
            } else {
                const isShowingModal = !!(
                    document.getElementsByClassName('ant-modal-wrap') &&
                    document.getElementsByClassName('ant-modal-wrap').length > 0
                );
                if (isShowingModal) {
                    return;
                }
                const times = getShowKiwiPlanAdvertiseTimes();
                if (times === '2') {
                    setGlobalLayer('showKiwiPlanAdvertisingTimeIntervalFlag', false);
                    if (showKiwiAdvertiseIntervalRef.current) {
                        window.clearInterval(showKiwiAdvertiseIntervalRef.current);
                    }
                    return;
                }
                if (!times) {
                    setShowKiwiPlanAdvertiseTimes('1');
                }
                if (times === '1') {
                    setShowKiwiPlanAdvertiseTimes('2');
                }
                setGlobalLayer('showKiwiPlanAdvertisingTimeIntervalFlag', false);
                openKiwiPlanAdvertisingModal();
            }
        };
        if (showKiwiPlanAdvertisingTimeIntervalFlag) {
            if (showKiwiAdvertiseIntervalRef.current) {
                window.clearInterval(showKiwiAdvertiseIntervalRef.current);
            } else {
                showKiwiAdvertiseIntervalRef.current = window.setInterval(callback, 5500);
            }
        } else if (showKiwiAdvertiseIntervalRef.current) {
            window.clearInterval(showKiwiAdvertiseIntervalRef.current);
        }
        return () => {
            if (showKiwiAdvertiseIntervalRef.current) {
                window.clearInterval(showKiwiAdvertiseIntervalRef.current);
            }
        };
    }, [showKiwiPlanAdvertisingTimeIntervalFlag]);

    if (isOpPortal()) {
        return null;
    }
    let width: string = '88%';
    if (window.screen.availWidth * 0.88 > 824) {
        width = '824px';
    }
    return (
        <div className={`${isShowKlarityUi ? s.layout : ''}`}>
            {isShowKlarityUi && (
                <Helmet>
                    <title>Klarity Health</title>
                    <link rel="icon" href="./favicon-klarity.ico" />
                </Helmet>
            )}
            <Modal
                width="86%"
                style={{ maxWidth: '1200px' }}
                open={getGlobalLayer('showSubscriptionModal')}
                //open
                title={null}
                footer={null}
                destroyOnClose
                onCancel={() => {
                    closeSubscriptionModal();
                }}
                // className={`${commonS.modalGlobalLayerWrap} ${commonS.modalGlobalLayerContentWithScrollWrap}`}
                className={`${commonS.modalGlobalLayerWrap} ${commonS.modalGlobalLayerContentWithTopPadding}`}
            >
                <Subscription />
            </Modal>
            <Modal
                width="90%"
                open={getGlobalLayer('showManageSubscriptionModal')?.show}
                //open
                title={null}
                footer={null}
                destroyOnClose
                onCancel={() => {
                    closeManageSubscriptionModal();
                }}
                className={commonS.modalGlobalLayerWrap}
                wrapClassName="manage-subscription-modal-wrap"
            >
                <ManageSubscription
                    isKlarityAccount={isShowKlarityUi}
                    isShowSwitchAccount={isShowSwitchAccount}
                    tabKey={getGlobalLayer('showManageSubscriptionModal')?.tabKey}
                />
            </Modal>
            <Modal
                width={width}
                open={getGlobalLayer('showSubscriptionSuccess')}
                title={null}
                footer={null}
                onCancel={() => {
                    sessionStorage.removeItem(IS_BACK_FROM_STRIPE);
                    closeSubscriptionSuccessModal();
                }}
                className={commonS.modalGlobalLayerWrap}
            >
                <SubscriptionSuccess
                    isKlarity={isKlarity}
                    onDone={() => {
                        sessionStorage.removeItem(IS_BACK_FROM_STRIPE);
                        closeSubscriptionSuccessModal();
                        openUniprofileAgreementSignModal('Confirm agreement of service', true);
                    }}
                    onClose={() => {
                        sessionStorage.removeItem(IS_BACK_FROM_STRIPE);
                        closeSubscriptionSuccessModal();
                    }}
                />
            </Modal>
            <Modal
                width="80%"
                open={getGlobalLayer('showSubscriptionTalk')}
                title={null}
                footer={null}
                onCancel={() => {
                    closeSubscriptionTalkModal();
                }}
                className={commonS.modalGlobalLayerWrap}
            >
                <SubscriptionTalk
                    onDone={() => {
                        closeSubscriptionTalkModal();
                    }}
                />
            </Modal>

            <Modal
                width="696px"
                open={getGlobalLayer('showChannelSubscriptionModal')}
                title={null}
                footer={null}
                onCancel={() => {
                    closeChannelSubscriptionModal();
                }}
                className={`${commonS.modalGlobalLayerWrap} ${commonS.modalWithRadius} ${commonS.modalGlobalLayerNoPaddingBottom}`}
            >
                {isPPAChannel ? (
                    <PPAChannelSubscription
                        onConfirm={() => {
                            closeChannelSubscriptionModal();
                        }}
                    />
                ) : (
                    <ChannelSubscription
                        onConfirm={() => {
                            closeChannelSubscriptionModal();
                        }}
                    />
                )}
            </Modal>
            <Modal
                width="86%"
                open={getGlobalLayer('showKlaritySubscriptionModal')}
                title={null}
                footer={null}
                onCancel={() => {
                    closeKlaritySubscriptionModal();
                }}
                className={`${commonS.modalGlobalLayerWrap} ${commonS.modalGlobalLayerContentWithScrollWrap}`}
            >
                <KlairtySubscription />
            </Modal>

            {getGlobalLayer('showKlarityPlanAdvertising')?.show && <KlarityPlanModal />}

            {/* <Modal
                width={992}
                className={s.avModal}
                title={null}
                open={getGlobalLayer('showKiwiPlanAdvertisingModal') && !dontShowAdvertisingPaht.includes(window?.location?.pathname)}
                footer={null}
                onCancel={() => {
                    closeKiwiPlanAdvertisingModal();
                }}
                maskClosable={false}
            >
                <KiwiPlanAdvertise />
            </Modal> */}
            <Modal
                width={992}
                destroyOnClose
                open={getGlobalLayer('showBlackFriday2024EnhancementModal')}
                footer={null}
                maskClosable={false}
                onCancel={() => {
                    closeBlackFriday2024EnhancementModal();
                }}
            >
                <EnhancementModal />
            </Modal>
            <Modal
                width={992}
                open={getGlobalLayer('showBlackFriday2024JoinCampaignModal')}
                footer={null}
                destroyOnClose
                maskClosable={false}
                onCancel={() => {
                    closeBlackFriday2024JoinCampaignModal();
                }}
            >
                <JoinCampaignModal />
            </Modal>
            {getGlobalLayer('showBlackFriday2024LeaveCampaignModal') && <LeavCampaignModal />}
            <Modal
                width="80vw"
                destroyOnClose
                title="Terms & Conditions"
                open={getGlobalLayer('showTermsNConditionsModal')}
                className={commonS.modalFixHeightFullScreenWrap}
                footer={null}
                onCancel={() => {
                    closeTermsNConditionsModal();
                }}
            >
                <TermsNConditions />
            </Modal>
            <Modal
                width="80vw"
                destroyOnClose
                title="Care Commitment Card Terms and Conditions"
                open={getGlobalLayer('showCCCTermsNConditionsModal')}
                className={commonS.modalFixHeightFullScreenWrap}
                footer={null}
                onCancel={() => {
                    closeCCCTermsNConditionsModal();
                }}
            >
                <CCCTermsNConditions />
            </Modal>

            {/* Campaign Modal template start*/}
            <Modal
                width={992}
                destroyOnClose
                open={getGlobalLayer('showCampaignModal')}
                footer={null}
                maskClosable={false}
                onCancel={() => {
                    closeCampaignModal();
                }}
            >
                <JoinCampaignModalTemplate />
            </Modal>
            {getGlobalLayer('showLeaveCampaignModal') && <LeavCampaignModalTemplate />}
            <Modal
                width="80vw"
                destroyOnClose
                title="Terms & Conditions"
                open={getGlobalLayer('showCampaignTermsNConditionsModal')}
                className={commonS.modalFixHeightFullScreenWrap}
                footer={null}
                onCancel={() => {
                    closeCampaignTermsNConditionsModal();
                }}
            >
                <CampaignTermsNConditions />
            </Modal>
            {/* Campaign Modal template end*/}
            <TextVisitUnSubmittedDrawer
                show={getGlobalLayer('showTextVisitUnSubmittedDrawer')?.show || false}
                textVisitUuid={getGlobalLayer('showTextVisitUnSubmittedDrawer')?.textVisitUuid}
                onClose={() => closeTextVisitUnSubmittedDrawer()}
                onSave={() => {
                    getGlobalLayer('showTextVisitUnSubmittedDrawer')?.onSave?.();
                    closeTextVisitUnSubmittedDrawer();
                }}
                fullyWithSaveFun={getGlobalLayer('showTextVisitUnSubmittedDrawer')?.fullyWithSaveFun}
                overrideProviderId={getGlobalLayer('showTextVisitUnSubmittedDrawer')?.overrideProviderId}
                readOnly={getGlobalLayer('showTextVisitUnSubmittedDrawer')?.readOnly}
            />
            <TextVisitSubmittedDrawer
                open={getGlobalLayer('showTextVisitSubmittedDrawer')?.show || false}
                textVisitUuid={getGlobalLayer('showTextVisitSubmittedDrawer')?.textVisitUuid}
                onClose={() => closeTextVisitSubmittedDrawer()}
                overrideProviderId={getGlobalLayer('showTextVisitSubmittedDrawer')?.overrideProviderId}
            />
            <VideoVisitSubmittedDrawer
                show={getGlobalLayer('showVideoVisitSubmittedDrawer')?.show || false}
                appointmentId={getGlobalLayer('showVideoVisitSubmittedDrawer')?.appointmentId}
                onClose={() => closeVideoVisitSubmittedDrawer()}
            />
            <VideoVisitUnSubmittedDrawer
                show={getGlobalLayer('showVideoVisitUnSubmittedDrawer')?.show || false}
                appointmentId={getGlobalLayer('showVideoVisitUnSubmittedDrawer')?.appointmentId}
                onClose={() => closeVideoVisitUnSubmittedDrawer()}
                onSave={() => {
                    getGlobalLayer('showVideoVisitUnSubmittedDrawer')?.onSave?.();
                    closeVideoVisitUnSubmittedDrawer();
                }}
            />
            <NonAppointmentSubmittedVisitNoteDrawer
                show={getGlobalLayer('showNonAppointmentSubmittedDrawer')?.show || false}
                noteId={getGlobalLayer('showNonAppointmentSubmittedDrawer')?.noteId}
                onClose={() => closeNonAppointmentSubmittedDrawer()}
            />
        </div>
    );
};

export default GlobalLayer;
